import { handleResponseErrors } from '@restDataAccess/handleResponseErrors';
import { applyRouteParams, getValidId, type WithId } from '@restDataAccess/withId';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import type { ApiNameType } from '@/constants';

import { authorizationHeader } from '../../authorizationHeader.ts';
import { useClearCacheCallback } from '../cacheHooks.ts';
import { assertApiBaseForFetch } from '../getApiUrl/getApiUrl.functions.ts';
import { useGetApiUrl } from '../useGetApiUrl.ts';

type AuthApiUpdateById = {
  baseApiRoute: ApiNameType;
  cacheKeys: string[];
  headers?: HeadersInit;
  identifier?: string;
  putOrPatch?: 'PUT' | 'PATCH';
  route: string;
  returnJsonResult?: boolean;
  /**
   * Extra `{placeholder}` values (e.g. `siteId`) resolved when the hook is created.
   * `{id}` and `{inventoryId}` are the item id `getValidId` selects from the body.
   */
  pathParams?: Record<string, string | undefined>;
};

/**
 * Custom hook for updating a resource by ID using authenticated API calls.
 *
 * @template T - The type of the resource being updated, must extend WithId
 * @template V - Optional return type of the update operation. Defaults to T if not specified
 *
 * @param {AuthApiUpdateById} params - Configuration object for the update operation
 * @param {ApiNameType} params.baseApiRoute - The base API route identifier
 * @param {string[]} params.cacheKeys - Cache keys to invalidate after update
 * @param {HeadersInit} [params.headers] - Optional additional headers for the request
 * @param {string} [params.identifier] - Optional identifier for logging purposes
 * @param {'PUT' | 'PATCH'} [params.putOrPatch='PUT'] - HTTP method to use for the update
 * @param {string} params.route - API route with placeholders (e.g. `/devices/{id}` or
 *   `/site/{siteId}/item/{inventoryId}`)
 * @param {boolean} [params.returnJsonResult] - Whether to return JSON result from response
 * @param {Record<string, string | undefined>} [params.pathParams] - Extra placeholders such as
 *   `{siteId}`. `{id}` and `{inventoryId}` stay the item id selected from the body.
 *
 * @returns A mutation hook for updating resources by ID with automatic cache invalidation
 */
export const useAuthApiUpdateById = <T extends WithId, V = void>({
  baseApiRoute,
  cacheKeys,
  headers,
  identifier,
  putOrPatch = 'PUT',
  route,
  returnJsonResult,
  pathParams,
}: AuthApiUpdateById) => {
  const auth = useAuth();
  const api = useGetApiUrl(baseApiRoute);
  const clearCacheCallback = useClearCacheCallback(cacheKeys);
  const updateDevice = async (device: Partial<T> & WithId & { name?: string }) => {
    const deviceName = identifier ?? device.name;
    try {
      const base = assertApiBaseForFetch(api);
      const itemId = getValidId(device);
      const resolvedRoute = applyRouteParams(route, {
        ...pathParams,
        id: itemId,
        // A spread inventory node can carry an `inventoryId` that is not the item being
        // edited. Both placeholders name that item, so use the id `getValidId` selected.
        inventoryId: pathParams?.inventoryId ?? itemId,
      });
      if (/\{[^}]+\}/.test(resolvedRoute)) {
        throw new Error(`Unresolved path params in route ${resolvedRoute}`);
      }
      const response = await fetch(`${base}${resolvedRoute}`, {
        method: putOrPatch,
        body: JSON.stringify(device),
        headers: {
          ...authorizationHeader(auth.user),
          'Content-Type': 'application/json',
          ...headers,
        },
      });

      return await handleResponseErrors<V>(response, deviceName, returnJsonResult);
    } catch (error) {
      console.error(`Failed to update device ${deviceName}`, error);
      throw error;
    }
  };

  return useMutation({
    mutationFn: updateDevice,
    onSuccess: () => clearCacheCallback(),
    onError: () => clearCacheCallback(),
  });
};
